import React from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { NavLink, Link } from 'react-router-dom'

import { IconMap, IconTracks, IconFlora, IconFauna, IconMore } from './icons'
import { color, bp, boxShadow, boxShadowActive } from '../globalStyles'
import { Container, Flex } from './common'
import Logo from './Logo'

const BackIcon = ({ className = '', ...props }) => (
  <svg
    xmlns='http://www.w3.org/2000/svg'
    width='24'
    height='24'
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='1'
    strokeLinecap='round'
    strokeLinejoin='round'
    className={`BackIcon feather feather-arrow-left-circle ${className}`}
    {...props}
  >
    <circle cx='12' cy='12' r='10' fill='white' stroke='none' />
    <polyline points='12 8 8 12 12 16' />
    <line x1='16' y1='12' x2='8' y2='12' />
  </svg>
)

export const navHeight = '6.4rem'

export const NavSpacer = styled.div`
  height: ${navHeight};
`
const NavSimple = styled.nav`
  color: ${props => props.color || 'white'};
  position: absolute;
  top: 0;
  left: 0;
  z-index: 10;
  width: 100%;
  display: flex;
  pointer-events: none;

  @media (${bp.desktop}) {
    display: none;
  }
`
const BackButton = styled(Link)`
  color: ${color.black};
  font-size: 3rem;
  width: 3rem;
  height: 3rem;
  margin: 1rem;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: all;
  box-shadow: ${boxShadow};
  border-radius: 50%;
  opacity: 0.9;
  transition: all 0.15s ease;

  .BackIcon {
    width: 100%;
    height: 100%;
  }

  &:hover,
  &:focus {
    box-shadow: ${boxShadowActive};
    transform: translateY(-1px);
    opacity: 1;
  }
`
const NavWrap = styled.nav`
  position: fixed;
  bottom: 0;
  left: 0;
  z-index: 10;
  background: white;
  overflow: hidden;
  height: ${navHeight};
  width: 100%;
  box-shadow: 0 0 6px 0 rgba(0, 0, 0, 0.04), 0 0 2px 0 rgba(0, 0, 0, 0.04),
    0 0 2px 0 rgba(0, 0, 0, 0.18);

  @media (${bp.desktop}) {
    position: relative;
    box-shadow: none;
    display: flex;
    align-items: center;

    ${Flex} {
      width: 100%;
      align-items: center;
      justify-content: center;
    }
  }
`
const NavItem = styled(NavLink)`
  color: ${color.darkGrey};
  padding: 1rem 0.5rem;
  position: relative;
  display: block;
  text-decoration: none;
  font-size: 1.2rem;
  font-weight: 500;
  width: 100%;
  text-align: center;

  .Icon {
    color: ${color.darkGrey};
  }

  &.active {
    opacity: 1;
    outline: none;
    color: ${color.primary};
  }

  &.hideOnMobile {
    display: none;
  }

  @media (${bp.desktop}) {
    font-size: 1.35rem;
    color: inherit;
    width: auto;
    padding: 1rem 1.5rem;
    position: relative;

    &.hideOnDesktop {
      display: none;
    }

    &.hideOnMobile {
      display: flex;
    }

    &.active {
      color: inherit;

      &:not(.noBorder):after {
        content: '';
        display: block;
        position: absolute;
        bottom: 0.5rem;
        left: 50%;
        transform: translateX(-50%);
        width: 2.5rem;
        border-bottom: 1px solid currentColor;
      }
    }
  }
`
const Icon = styled.div`
  margin: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 2.8rem;

  svg {
    width: 2.8rem;
    height: 2.8rem;
  }

  @media (${bp.desktop}) {
    display: none;
  }
`
const NavIcon = ({ icon }) => {
  let Svg
  switch (icon) {
    case 'discover':
      Svg = Logo
      break
    case 'tracks':
      Svg = IconTracks
      break
    case 'flora':
      Svg = IconFlora
      break
    case 'fauna':
      Svg = IconFauna
      break
    case 'more':
      Svg = IconMore
      break
    default:
      Svg = IconMap
  }
  return (
    <Icon>
      <Svg />
    </Icon>
  )
}
const NavLogoItem = styled(NavItem)`
  margin-right: auto;
  display: flex;
  align-items: center;
  justify-content: center;
`
const NavLogo = styled(Logo)`
  width: auto;
  height: 30px;
  color: ${color.black};
  fill: currentColor;

  .active & path {
    color: ${color.black};
    fill: currentColor;
  }
`

const Nav = ({
  parentPath = '/',
  backButton,
  route,
  color,
  flora,
  fauna,
  tracks
}) => {
  if (backButton) {
    // Check if history is from this domain
    const externalReferrer = route.history.action === 'POP'
    return (
      <NavSimple color={color}>
        <BackButton
          to={parentPath}
          onClick={e => {
            // go back if internal page referrer
            if (!externalReferrer) {
              e.preventDefault()
              route.history.goBack()
            }
          }}
        >
          <BackIcon />
        </BackButton>
      </NavSimple>
    )
  }
  return (
    <NavWrap>
      <Container noMax>
        <Flex alignCenter justifyAround>
          <NavLogoItem
            className='hideOnMobile noBorder'
            exact
            to='/'
            title='Discover Springbrook'
          >
            <NavLogo full />
          </NavLogoItem>
          <NavItem exact to='/'>
            <NavIcon icon='discover' />Discover
          </NavItem>
          {!!tracks.length && (
            <NavItem to='/walks'>
              <NavIcon icon='tracks' />Walks
            </NavItem>
          )}
          {!!flora.length && (
            <NavItem to='/flora'>
              <NavIcon icon='flora' />Flora
            </NavItem>
          )}
          {!!fauna.length && (
            <NavItem to='/fauna'>
              <NavIcon icon='fauna' />Fauna
            </NavItem>
          )}
          <NavItem to='/map'>
            <NavIcon icon='map' />Map
          </NavItem>
          <NavItem to='/more' className='hideOnDesktop'>
            <NavIcon icon='more' />More
          </NavItem>
        </Flex>
      </Container>
    </NavWrap>
  )
}

Nav.propTypes = {
  route: PropTypes.object.isRequired,
  parentPath: PropTypes.string,
  color: PropTypes.string,
  flora: PropTypes.array,
  fauna: PropTypes.array,
  tracks: PropTypes.array
}

export default Nav



// WEBPACK FOOTER //
// ./src/components/Nav.js